import React, { memo, useCallback } from 'react';
import { StyleSheet } from 'react-native';

import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Column } from '@/components/lib/components';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { isPlayerSelected } from '@/feature/betslip-pickem/utils/betslip-utils';
import { useOpenPlayerProfile } from '@/feature/player-profile-sheets/hooks/use-open-player-profile-sheet';
import { LobbyCarousel } from '@/feature/promohub/components/LobbyCarousel';
import { common } from '@/styles/styles';
import { ProductDocumentDataBodyLobbycomponentsSliceItem } from '@/types/prismic.generated';
import { logger } from '@/utils/logging';

import { FavouritesList } from '../components/FavoritesList';
import { FeaturedLineupSection } from '../components/FeaturedLineupSection';
import { PlayerRow } from '../components/PlayerRow';
import { PopularPlayersList } from '../components/PopularPlayersList';
import { LobbyComponent } from '../utils/lobbySections';
import { PickSelectionMethods, usePickSelection } from './use-pick-selection';

/**
 * Hook responsible for rendering the lobby content
 */
export const useLobbyContentRender = ({ players }: { players: PlayerWithTeamAndEvent[] }) => {
    const { makeSelection, removeSelection } = usePickSelection();
    const { openPlayerProfile } = useOpenPlayerProfile(players);

    const renderLobbyPlayerItem = useCallback(
        ({ item, index, title }: { item: PlayerWithTeamAndEvent; title: string; index: number }) => {
            const isFirstItem = players.findIndex(it => it.id === item.id) === 0;
            const isLastItem = players.findIndex(it => it.id === item.id) === players.length - 1;
            return (
                <Column testID={`trendingPlayer-${index}`} paddingHorizontal={'s16'}>
                    {isFirstItem ? (
                        <>
                            <Text variant="headlineMedium" style={styles.title}>
                                {title}
                            </Text>
                            <SizedBox value={8} />
                        </>
                    ) : null}
                    <MemoPlayerRow
                        player={item}
                        makeSelection={makeSelection}
                        removeSelection={removeSelection}
                        renderBottomSeparator={!isLastItem}
                        openPlayerProfile={openPlayerProfile}
                    />
                </Column>
            );
        },
        [makeSelection, openPlayerProfile, players, removeSelection]
    );

    const renderLobbyComponent = useCallback((component: ProductDocumentDataBodyLobbycomponentsSliceItem) => {
        switch (component.orderingkey) {
            case LobbyComponent.PopularPlayers:
                return <PopularPlayersList title={component.header as string} />;
            case LobbyComponent.PromoCarousel:
                return <LobbyCarousel style={styles.promoCarousel} />;
            case LobbyComponent.FeaturedLineups:
                return <FeaturedLineupSection title={component.header as string} />;
            case LobbyComponent.FavoritesPlayers:
                return <FavouritesList title={component.header as string} />;
            default:
                if (component.orderingkey && ['leagues', 'trending_players'].includes(component.orderingkey)) {
                    //Leagues can not be ordered anymore, as they stick under search always
                    //Trending players can not be ordered anymore, will always be last in the list
                } else {
                    logger.debug(`Unrecognized lobby component ordering key: ${component.orderingkey}`);
                }
                return null;
        }
    }, []);

    return {
        renderLobbyPlayerItem,
        renderLobbyComponent,
    };
};

const MemoPlayerRow = memo(
    ({
        renderBottomSeparator,
        player,
        removeSelection,
        makeSelection,
        openPlayerProfile,
    }: {
        player: PlayerWithTeamAndEvent;
        renderBottomSeparator: boolean;
        openPlayerProfile: (playerId: string) => void;
    } & PickSelectionMethods) => {
        const playerPickedEntry = isPlayerSelected({
            eventId: player?.event.id,
            playerId: player?.id,
        });

        return (
            <Box>
                {playerPickedEntry ? (
                    <PlayerRow
                        player={player}
                        mode="selection"
                        outcome={playerPickedEntry.outcome}
                        projection={playerPickedEntry.projection}
                        event={player.event}
                        pressable={true}
                        analyticsTag={'trendingPlayers'}
                        removeSelection={removeSelection}
                        makeSelection={makeSelection}
                        openPlayerProfile={openPlayerProfile}
                    />
                ) : (
                    <PlayerRow
                        player={player}
                        mode="all"
                        event={player.event}
                        pressable={true}
                        analyticsTag={'trendingPlayers'}
                        removeSelection={removeSelection}
                        makeSelection={makeSelection}
                        openPlayerProfile={openPlayerProfile}
                    />
                )}
                {renderBottomSeparator ? <LineSeparator style={common.separator} /> : null}
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    title: {
        fontSize: 22,
    },
    promoCarousel: {
        paddingBottom: 28,
        paddingTop: 20,
    },
});
